import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  updateCreationObjectives,
  removeCreationObjective,
} from "../../../store/slices/projectObjectiveSlice";
import {
  Plus,
  X,
  ChevronDownIcon,
  ChevronUpIcon,
  Settings,
  Pencil,
  Trash2,
} from "lucide-react";
import { renderGrid } from "../../common/FormRenderer";
import { Modal, Button } from "antd";
import {
  objectiveSections,
  initialFormState,
  PO_PICKLIST_LABELS,
} from "../../../config/projectObjectiveFields";
import { validateProjectObjectiveEditForm } from "../../../config/projectObjectiveEdit.validation";
import {
  usePicklistMap,
  resolvePicklistFields,
} from "../../../hooks/usePicklist";
import { SOBJECT_PROJECT_OBJECTIVE } from "../../../config/sobjectNames";
import {
  buildRequirementsFieldsForCreate,
  buildProductivityFieldsForCreate,
} from "../../project-objective/columnsConfig";
import { getPreferredDefaultWorkTypeValue } from "../../../config/projectObjectiveDefaults.js";

const EMPTY_PICKLIST = [];

const ProjectObjectives = () => {
  const dispatch = useDispatch();
  const objectives = useSelector(
    (state) => state.projectObjective.creation.objectives,
  );
  const metadata = useSelector((state) => state.project.creation.metadata);
  const [showModal, setShowModal] = useState(false);

  const { picklistMap } = usePicklistMap(
    Object.values(PO_PICKLIST_LABELS),
    SOBJECT_PROJECT_OBJECTIVE,
  );
  const workTypeOptions =
    picklistMap[PO_PICKLIST_LABELS.WORK_TYPE] ?? EMPTY_PICKLIST;

  const [validationErrors, setValidationErrors] = useState([]);
  const [showAdvancedModal, setShowAdvancedModal] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    information: true,
    requirements: true,
    productivity: true,
    actionRules: true,
    funnel: false,
    rates: false,
    system: false,
  });

  const [advancedExpandedSections, setAdvancedExpandedSections] = useState({
    funnel: true,
    rates: false,
    system: false,
  });

  const [form, setForm] = useState({
    ...initialFormState,
    project: metadata?.name || "",
    dateStart: metadata?.hireStartDate || "",
    dateEnd: metadata?.predictedCloseDate || "",
  });

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const toggleAdvancedSection = (section) => {
    setAdvancedExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Define which sections are "advanced"
  const advancedSectionKeys = ["funnel", "rates", "system"];

  // Convert objectiveSections object to array and filter into basic and advanced
  const sectionsArray = Object.values(objectiveSections);
  const basicSections = sectionsArray.filter(
    (s) => !advancedSectionKeys.includes(s.key),
  );
  const advancedSections = sectionsArray.filter((s) =>
    advancedSectionKeys.includes(s.key),
  );

  const generateObjectiveName = (curForm) => {
    const provided = (curForm?.objectiveName || "").trim();
    if (provided) return provided;
    const contributorFacingName = (
      metadata?.contributorFacingName ||
      metadata?.shortName ||
      metadata?.name ||
      ""
    ).trim();
    const language = (curForm?.language || "").trim();
    const country = (curForm?.country || "").trim();
    const langCountry =
      language && country ? `${language}-${country}` : language || country;
    const appendToObjectiveName = (curForm?.appendToObjectiveName || "").trim();
    if (contributorFacingName && langCountry)
      return `${contributorFacingName}: ${langCountry}${appendToObjectiveName}`;
    if (contributorFacingName)
      return contributorFacingName`${appendToObjectiveName}`;
    if (langCountry) return langCountry`${appendToObjectiveName}`;
    return "Project Objective";
  };

  const validateForm = () => {
    // Use centralized validation
    const validationPayload = {
      info: {
        name: generateObjectiveName(form),
        contributorFacingName: form.contributorFacingName,
        dateStart: form.dateStart,
        dateEnd: form.dateEnd,
        // In the wizard we may not have a real Salesforce Id yet; pass a placeholder
        // so centralized validation treats the Project as provided.
        projectId: metadata?.id || form.projectId || "TEMP",
      },
      requirements: {
        workType: form.workType,
        country: form.country || "",
        language: form.language || "",
        excludeStates: form.excludeStates || "",
        listOfCountries: form.listOfCountries || "",
        currentlyResidingUsState: form.currentlyResidingUsState || "",
      },
      diversity: {
        minimumContributors: form.minimumContributors,
      },
      productivity: {
        productivityTargetType: form.productivityTargetType,
      },
      actionRules: {
        daysBetweenReminderEmails: form.daysBetweenReminderEmails,
      },
      targets: {
        productionPaymentThreshold: form.productionPaymentThreshold,
        qualityPaymentThreshold: form.qualityPaymentThreshold,
      },
      funnelSplits: {
        funnelA: form.funnelAPercentage,
        funnelB: form.funnelBPercentage,
        funnelC: form.funnelCPercentage,
        funnelD: form.funnelDPercentage,
        funnelE: form.funnelEPercentage,
      },
    };
    const validationErrors = validateProjectObjectiveEditForm(
      validationPayload,
      true,
    );

    // Convert centralized validation errors to wizard format with field labels
    const errors = [];
    Object.entries(validationErrors).forEach(([path, message]) => {
      // Map path to field name and label
      const fieldLabelMap = {
        "info.projectId": "Project",
        "info.name": "Objective Name",
        "info.contributorFacingName": "Contributor Facing Name",
        "info.dateStart": "Date Start",
        "info.dateEnd": "Date End",
        "requirements.workType": "Work Type",
        "requirements.country": "Country",
        "requirements.language": "Language",
        "diversity.minimumContributors": "Minimum Contributors",
        "productivity.productivityTargetType": "Productivity Target Type",
        "targets.productionPaymentThreshold": "Production Payment Threshold",
        "targets.qualityPaymentThreshold": "Quality Payment Threshold",
        "actionRules.daysBetweenReminderEmails": "Days Between Reminder Emails",
        "funnel.funnelAPercentage": "Funnel A (%)",
      };
      const fieldMap = {
        "info.projectId": "project",
        "info.name": "objectiveName",
        "info.contributorFacingName": "contributorFacingName",
        "info.dateStart": "dateStart",
        "info.dateEnd": "dateEnd",
        "requirements.workType": "workType",
        "requirements.country": "country",
        "requirements.language": "language",
        "diversity.minimumContributors": "minimumContributors",
        "productivity.productivityTargetType": "productivityTargetType",
        "targets.productionPaymentThreshold": "productionPaymentThreshold",
        "targets.qualityPaymentThreshold": "qualityPaymentThreshold",
        "actionRules.daysBetweenReminderEmails": "daysBetweenReminderEmails",
        "funnel.funnelAPercentage": "funnelAPercentage",
      };
      const field = fieldMap[path] || path;
      const fieldLabel = fieldLabelMap[path] || path;

      // Check if message already includes the field label (validation function includes it)
      // If message already contains the field label, use it as-is
      // Otherwise, prepend the field label for generic messages
      const messageAlreadyHasLabel =
        message.includes(fieldLabel) ||
        message.startsWith(fieldLabel.split(":")[0]);
      const needsFieldLabel =
        !messageAlreadyHasLabel &&
        [
          "This field is required",
          "Must be an integer",
          "Must be a number",
          "Invalid format",
        ].some((msg) => message.includes(msg));

      const finalMessage = needsFieldLabel
        ? `${fieldLabel}: ${message}`
        : message;

      errors.push({ field, message: finalMessage });
    });

    return errors;
  };

  const saveObjective = (shouldKeepOpen = false) => {
    console.log("💾 Saving objective with form data:", form);

    // Additional safety check: explicitly check workType before validation
    if (!form.workType || form.workType.trim() === "") {
      console.log("⚠️ Work Type is missing or empty, blocking save");
      setValidationErrors([
        {
          field: "workType",
          message: "Work Type: This field is required",
        },
      ]);
      return;
    }

    // Additional safety check: either country or language must be filled
    const country = form.country || "";
    const language = form.language || "";
    const countryIsEmpty =
      country === undefined ||
      country === null ||
      country === "" ||
      (typeof country === "string" && country.trim() === "");
    const languageIsEmpty =
      language === undefined ||
      language === null ||
      language === "" ||
      (typeof language === "string" && language.trim() === "");

    if (countryIsEmpty && languageIsEmpty) {
      console.log(
        "⚠️ Both Country and Language are missing or empty, blocking save",
      );
      setValidationErrors([
        {
          field: "country",
          message: "Either Country or Language must be filled",
        },
      ]);
      return;
    }

    const errors = validateForm();
    console.log("🔍 Validation errors:", errors);

    if (errors.length > 0) {
      console.log("❌ Validation failed, blocking save");
      setValidationErrors(errors);
      return;
    }

    setValidationErrors([]);

    const newObjective = {
      ...form,
      id: Date.now(),
      objectiveName: generateObjectiveName(form),
      // Ensure workType is explicitly included (even if empty string)
      workType: form.workType !== undefined ? form.workType : "",
      // Persist a projectId so centralized validation on step navigation succeeds
      projectId: form.projectId || metadata?.id || "TEMP",
    };

    console.log("✅ Validation passed, saving objective:", newObjective);
    console.log(
      "📝 WorkType in saved objective:",
      newObjective.workType,
      "Type:",
      typeof newObjective.workType,
    );

    const updatedObjectives = Array.isArray(objectives)
      ? [...objectives, newObjective]
      : [newObjective];
    dispatch(updateCreationObjectives(updatedObjectives));

    if (!shouldKeepOpen) {
      setShowModal(false);
      resetForm();
    } else {
      resetForm();
    }
  };

  const resetForm = () => {
    const defaultWorkType = getPreferredDefaultWorkTypeValue(workTypeOptions);
    setForm({
      ...initialFormState,
      id: undefined,
      project: metadata?.name || "",
      workType: defaultWorkType || "",
      dateStart: metadata?.hireStartDate || "",
      dateEnd: metadata?.predictedCloseDate || "",
    });
    setValidationErrors([]);
  };

  // If describe loads after the modal opens, apply preferred default once (new objective only).
  useEffect(() => {
    if (!showModal || form.id != null) return;
    const preferred = getPreferredDefaultWorkTypeValue(workTypeOptions);
    if (!preferred) return;
    setForm((prev) => {
      if (prev.id != null) return prev;
      const cur = (prev.workType ?? "").trim();
      if (cur !== "") return prev;
      return { ...prev, workType: preferred };
    });
  }, [showModal, workTypeOptions, form.id]);

  const openModal = () => {
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleChange = (path, value) => {
    setForm((prev) => {
      const updated = { ...prev, [path]: value };
      if (path === "country") {
        if (value !== "United States") {
          updated.excludeStates = false;
          updated.currentlyResidingUsState = [];
        }
        if (value !== "") {
          updated.includeInCountryList = false;
          updated.listOfCountries = "";
        }
      }
      return updated;
    });
  };

  const handleRemove = (id) => {
    if (
      window.confirm("Are you sure you want to remove this project objective?")
    ) {
      dispatch(removeCreationObjective(id));
    }
  };

  const handleEdit = (objective) => {
    // Ensure all required fields are present when editing
    // If workType is missing, set it to empty string so validation can catch it
    // Also check if workType value matches any valid option - if not, treat as empty

    const workTypePicklist = picklistMap[PO_PICKLIST_LABELS.WORK_TYPE] || [];

    let workTypeValue = "";
    if (objective.workType !== undefined && objective.workType !== null) {
      const trimmed = String(objective.workType).trim();
      if (trimmed) {
        if (workTypePicklist.length === 0) {
          workTypeValue = trimmed;
        } else {
          const match = workTypePicklist.find(
            (opt) =>
              opt.value === trimmed ||
              opt.label === trimmed ||
              (opt.value &&
                opt.value.toLowerCase() === trimmed.toLowerCase()) ||
              (opt.label && opt.label.toLowerCase() === trimmed.toLowerCase()),
          );
          workTypeValue = match ? match.value : "";

          if (!match) {
            console.warn(
              "Work Type value is not in the current Salesforce picklist:",
              trimmed,
            );
          }
        }
      } else {
        workTypeValue =
          getPreferredDefaultWorkTypeValue(workTypePicklist) || "";
      }
    } else {
      workTypeValue = getPreferredDefaultWorkTypeValue(workTypePicklist) || "";
    }

    const formData = {
      ...objective,
      project: metadata?.name || objective.project || "",
      objectiveName: "",
      // Ensure required fields exist (even if empty) for validation
      workType: workTypeValue,
      productivityTargetType:
        objective.productivityTargetType !== undefined &&
        objective.productivityTargetType !== null &&
        String(objective.productivityTargetType).trim() !== ""
          ? String(objective.productivityTargetType).trim()
          : "",
      daysBetweenReminderEmails:
        objective.daysBetweenReminderEmails !== undefined &&
        objective.daysBetweenReminderEmails !== null &&
        String(objective.daysBetweenReminderEmails).trim() !== ""
          ? String(objective.daysBetweenReminderEmails).trim()
          : "",
    };

    console.log("📝 Editing objective:");
    console.log("📝 Original objective:", objective);
    console.log(
      "📝 WorkType in original:",
      objective.workType,
      "Type:",
      typeof objective.workType,
    );
    console.log("📝 WorkType in formData:", formData.workType);
    console.log("📝 WorkType is empty:", formData.workType === "");
    console.log("📝 Full formData:", formData);

    setForm(formData);
    setShowModal(true);
    setValidationErrors([]);
  };

  const handleSaveEdit = (shouldKeepOpen = false) => {
    console.log("💾 Updating objective with form data:", form);
    console.log(
      "💾 WorkType value:",
      form.workType,
      "Type:",
      typeof form.workType,
    );
    console.log(
      "💾 WorkType isEmpty check:",
      !form.workType ||
        (typeof form.workType === "string" && form.workType.trim() === ""),
    );

    // Additional safety check: explicitly check workType before validation
    const workTypeValue = form.workType;
    const workTypeIsEmpty =
      workTypeValue === undefined ||
      workTypeValue === null ||
      workTypeValue === "" ||
      (typeof workTypeValue === "string" && workTypeValue.trim() === "");

    if (workTypeIsEmpty) {
      console.log("⚠️ Work Type is missing or empty, blocking update");
      console.log(
        "⚠️ WorkType value:",
        workTypeValue,
        "isEmpty:",
        workTypeIsEmpty,
      );
      setValidationErrors([
        {
          field: "workType",
          message: "Work Type: This field is required",
        },
      ]);
      return;
    }

    // Additional safety check: either country or language must be filled
    const country = form.country || "";
    const language = form.language || "";
    const countryIsEmpty =
      country === undefined ||
      country === null ||
      country === "" ||
      (typeof country === "string" && country.trim() === "");
    const languageIsEmpty =
      language === undefined ||
      language === null ||
      language === "" ||
      (typeof language === "string" && language.trim() === "");

    if (countryIsEmpty && languageIsEmpty) {
      console.log(
        "⚠️ Both Country and Language are missing or empty, blocking update",
      );
      setValidationErrors([
        {
          field: "country",
          message: "Either Country or Language must be filled",
        },
      ]);
      return;
    }

    const errors = validateForm();
    console.log("🔍 Validation errors:", errors);

    if (errors.length > 0) {
      console.log("❌ Validation failed, blocking update");
      setValidationErrors(errors);
      return;
    }

    setValidationErrors([]);

    const updatedObjectives = objectives.map((obj) =>
      obj.id === form.id
        ? {
            ...form,
            objectiveName: generateObjectiveName(form),
            // Ensure workType is explicitly included (even if empty string)
            workType: form.workType !== undefined ? form.workType : "",
          }
        : obj,
    );

    console.log(
      "✅ Validation passed, updating objectives:",
      updatedObjectives,
    );
    console.log(
      "📝 Updated objective workType:",
      updatedObjectives.find((obj) => obj.id === form.id)?.workType,
    );

    dispatch(updateCreationObjectives(updatedObjectives));

    if (!shouldKeepOpen) {
      setShowModal(false);
      resetForm();
    } else {
      resetForm();
    }
  };

  const SectionHeader = ({ title, section }) => (
    <button
      type="button"
      onClick={() => toggleSection(section)}
      className="w-full flex items-center justify-between p-2.5 bg-gray-50 hover:bg-gray-100 rounded-md transition-colors"
    >
      <h4 className="text-sm font-semibold text-gray-700">{title}</h4>
      {expandedSections[section] ? (
        <ChevronUpIcon className="w-4 h-4 text-gray-400" />
      ) : (
        <ChevronDownIcon className="w-4 h-4 text-gray-400" />
      )}
    </button>
  );

  return (
    <div className="fade-in">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Project Objectives
        </h2>
        <p className="text-gray-600">
          Define objectives and requirements for contributors
        </p>
      </div>

      {/* Current Objectives List */}
      {objectives.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">
            Current Objectives ({objectives.length})
          </h3>
          <div className="space-y-3">
            {objectives.map((obj) => (
              <div
                key={obj.id}
                className="p-4 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-between group"
              >
                <div className="flex-1">
                  <div className="font-medium text-gray-900">
                    {obj.objectiveName}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    Work Type: {obj.workType} | Country: {obj.country || "N/A"}{" "}
                    | Language: {obj.language || "N/A"}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button
                    type="button"
                    onClick={() => handleEdit(obj)}
                    className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                    title="Edit project objective"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRemove(obj.id)}
                    className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                    title="Remove project objective"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* New Objective Button */}
      <button
        type="button"
        onClick={openModal}
        className="flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-colors"
        style={{ backgroundColor: "#08979c" }}
        onMouseEnter={(e) => (e.target.style.backgroundColor = "#0ab0b7")}
        onMouseLeave={(e) => (e.target.style.backgroundColor = "#08979c")}
      >
        <Plus className="w-5 h-5" />
        New Project Objective
      </button>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-900">
                {form.id ? "Edit Project Objective" : "New Project Objective"}
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Validation Errors */}
              {validationErrors.length > 0 && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <h4 className="text-red-800 font-semibold mb-2">
                    Please fix the following errors:
                  </h4>
                  <ul className="list-disc list-inside text-red-700 text-sm space-y-1">
                    {validationErrors.map((error, index) => (
                      <li key={index}>{error.message}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Basic Sections */}
              <div className="space-y-4">
                {basicSections.map((section) => (
                  <div key={section.key}>
                    <SectionHeader
                      title={section.title}
                      section={section.key}
                    />
                    {expandedSections[section.key] && (
                      <div className="mt-3">
                        {renderGrid(
                          resolvePicklistFields(
                            section.key === "requirements"
                              ? buildRequirementsFieldsForCreate({ form })
                              : section.key === "productivity"
                                ? buildProductivityFieldsForCreate(form)
                                : section.fields,
                            picklistMap,
                          ),
                          {
                            formData: form,
                            onChange: handleChange,
                            showValidation: validationErrors.length > 0,
                          },
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Advanced Fields Button */}
              <div className="mt-6">
                <button
                  type="button"
                  onClick={() => setShowAdvancedModal(true)}
                  className="w-full px-4 py-3 border-2 rounded-lg flex items-center justify-center gap-2 transition-all duration-200 hover:shadow-md"
                  style={{
                    borderColor: "#08979c",
                    color: "#08979c",
                    backgroundColor: "white",
                  }}
                >
                  <Settings className="w-5 h-5" />
                  <span className="font-medium">Advanced Fields</span>
                  <span className="text-sm text-gray-500">(Optional)</span>
                </button>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={closeModal}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              {!form.id && (
                <button
                  type="button"
                  onClick={() => saveObjective(true)}
                  className="px-4 py-2 text-white rounded-lg transition-colors"
                  style={{ backgroundColor: "#08979c" }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#0ab0b7")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "#08979c")
                  }
                >
                  Save & New
                </button>
              )}
              <button
                type="button"
                onClick={() =>
                  form.id ? handleSaveEdit(false) : saveObjective(false)
                }
                className="px-4 py-2 text-white rounded-lg transition-colors"
                style={{ backgroundColor: "#08979c" }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#0ab0b7")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "#08979c")
                }
              >
                {form.id ? "Update" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Advanced Fields Modal */}
      <Modal
        title="Advanced Fields"
        open={showAdvancedModal}
        onCancel={() => setShowAdvancedModal(false)}
        footer={[
          <Button
            key="close"
            type="primary"
            onClick={() => setShowAdvancedModal(false)}
          >
            Done
          </Button>,
        ]}
        width={900}
        bodyStyle={{ maxHeight: "70vh", overflowY: "auto" }}
      >
        <div className="space-y-3">
          {advancedSections.map((section) => {
            const AdvancedSectionHeader = ({ title, sectionKey }) => (
              <button
                type="button"
                onClick={() => toggleAdvancedSection(sectionKey)}
                className="w-full flex items-center justify-between p-2.5 bg-gray-50 hover:bg-gray-100 rounded-md transition-colors"
              >
                <span className="text-sm font-semibold text-gray-700">
                  {title}
                </span>
                {advancedExpandedSections[sectionKey] ? (
                  <ChevronUpIcon className="w-4 h-4 text-gray-400" />
                ) : (
                  <ChevronDownIcon className="w-4 h-4 text-gray-400" />
                )}
              </button>
            );

            return (
              <div key={section.key}>
                <AdvancedSectionHeader
                  title={section.title}
                  sectionKey={section.key}
                />
                {advancedExpandedSections[section.key] && (
                  <div className="mt-2">
                    {renderGrid(
                      resolvePicklistFields(section.fields, picklistMap),
                      {
                        formData: form,
                        onChange: handleChange,
                        showValidation: validationErrors.length > 0,
                      },
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Modal>
    </div>
  );
};

export default ProjectObjectives;
